'use client'

import { FormBlock as FormBlockType, Form } from '@onclicks/payload-types'

import RichText from '@onclicks/sharedcomponents/RichText'
import { useRouter } from 'next/navigation'
import React, { useCallback, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { Button } from '../components/ui/Button'

import { getClientSideURL } from '@onclicks/utilities/getURL'
import { fields } from '../components/FieldForm/fields'

export const FormBlock: React.FC<FormBlockType> = (props) => {
  const { enableIntro, form: formFromProps, introContent } = props

  const isFormObject = typeof formFromProps === 'object' && formFromProps !== null
  const form = isFormObject ? (formFromProps as Form) : null

  const formID = form?.id
  const confirmationMessage = form?.confirmationMessage
  const confirmationType = form?.confirmationType
  const redirect = form?.redirect
  const submitButtonLabel = form?.submitButtonLabel

  const formMethods = useForm({
    defaultValues: form?.fields as unknown as Record<string, string>,
  })
  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    reset,
  } = formMethods

  const [isLoading, setIsLoading] = useState(false)
  const [hasSubmitted, setHasSubmitted] = useState<boolean>()
  const [error, setError] = useState<{ message: string; status?: string } | undefined>()
  const router = useRouter()

  const onSubmit = useCallback(
    (data: Record<string, string>) => {
      let loadingTimerID: ReturnType<typeof setTimeout>
      if (!formID) return

      const submitForm = async () => {
        setError(undefined)
        const dataToSend = Object.entries(data).map(([name, value]) => ({
          field: name,
          value,
        }))

        loadingTimerID = setTimeout(() => {
          setIsLoading(true)
        }, 1000)

        try {
          const req = await fetch(`${getClientSideURL()}/api/form-submissions`, {
            body: JSON.stringify({
              form: formID,
              submissionData: dataToSend,
            }),
            headers: {
              'Content-Type': 'application/json',
            },
            method: 'POST',
          })

          const res = await req.json()

          clearTimeout(loadingTimerID)

          if (req.status >= 400) {
            setIsLoading(false)

            setError({
              message: res.errors?.[0]?.message || 'Internal Server Error',
              status: res.status,
            })

            return
          }

          setIsLoading(false)
          setHasSubmitted(true)

          if (confirmationType === 'redirect' && redirect) {
            const { url } = redirect

            const redirectUrl = url

            if (redirectUrl) router.push(redirectUrl)
          }
        } catch (err) {
          console.warn(err)
          setIsLoading(false)
          setError({
            message: 'Something went wrong.',
          })
        }
      }

      void submitForm()
    },
    [router, formID, redirect, confirmationType],
  )

  if (!form) return null

  return (
    <div className="w-full">
      {enableIntro && introContent && !hasSubmitted && (
        <RichText className="mb-8 lg:mb-12" data={introContent} enableGutter={false} />
      )}
      <div>
        <FormProvider {...formMethods}>
          {!isLoading && hasSubmitted && confirmationType === 'message' && confirmationMessage && (
            <RichText data={confirmationMessage} />
          )}
          {isLoading && !hasSubmitted && <p>Loading, please wait...</p>}
          {error && <div>{`${error.status || '500'}: ${error.message || ''}`}</div>}
          {!hasSubmitted && (
            <form id={formID?.toString()} onSubmit={handleSubmit(onSubmit)}>
              <div className="mb-4 flex flex-wrap justify-between gap-x-8 gap-y-6 last:mb-0">
                {form &&
                  form.fields &&
                  form.fields?.map((field, index) => {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const Field: React.FC<any> = fields?.[field.blockType as keyof typeof fields]
                    if (Field) {
                      const hasWidth = field && 'width' in field
                      const widthValue = hasWidth ? (field as any).width : 100
                      // gap-x-8 déjà présent dans le flex parent, donc on retire
                      // la largeur exacte du gap pour que 2 champs à 50% tiennent bien
                      const fieldWidth =
                        widthValue && widthValue < 100 ? `calc(${widthValue}% - 1rem)` : '100%'

                      return (
                        <div
                          key={index}
                          style={{
                            width: fieldWidth,
                            flexGrow: 0,
                            flexShrink: 0,
                            boxSizing: 'border-box',
                          }}
                        >
                          <Field
                            form={form}
                            {...field}
                            {...formMethods}
                            control={control}
                            errors={errors}
                            register={register}
                          />
                        </div>
                      )
                    }
                    return null
                  })}
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-8">
                <Button form={formID?.toString()} type="submit" variant="default">
                  {submitButtonLabel || 'Envoyer'}
                </Button>

                <Button type="button" onClick={() => reset()} variant="outline">
                  Effacer
                </Button>
              </div>
            </form>
          )}
        </FormProvider>
      </div>
      <p className="mt-4 text-xs">
        En envoyant ce formulaire, j’accepte l’utilisation de mes données pour le traitement de ma
        demande.
      </p>
    </div>
  )
}
